import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Form,
  Input,
  Upload
} from 'antd';
import {
  IdcardOutlined,
  CreditCardOutlined,
  WechatOutlined,
  AlipayOutlined,
  UploadOutlined
} from '@ant-design/icons';
import ImageViewer from './ImageViewer';

// 四个图片字段及其展示名
const IMAGE_FIELD_LABELS = {
  id_card_front: '身份证正面',
  id_card_back: '身份证反面',
  wechat_qr_code: '微信收款码',
  alipay_qr_code: '支付宝收款码'
};

const EmployeeUploadCard = ({ form, basicEditMode = false, employeeId }) => {
  // 图片预览状态
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const [currentImage, setCurrentImage] = useState({ url: '', name: '', downloadUrl: '' });

  // 文件列表状态管理（Upload 受控组件，必须通过 fileList 才能渲染缩略图）
  const [fileLists, setFileLists] = useState({
    id_card_front: [],
    id_card_back: [],
    wechat_qr_code: [],
    alipay_qr_code: []
  });

  // 监听表单中已保存的图片 URL（员工数据异步加载、保存后回填都能响应）
  const watchedUrls = {
    id_card_front: Form.useWatch('id_card_front', form),
    id_card_back: Form.useWatch('id_card_back', form),
    wechat_qr_code: Form.useWatch('wechat_qr_code', form),
    alipay_qr_code: Form.useWatch('alipay_qr_code', form)
  };

  // 将表单中已保存的 URL 同步成 Upload 的 fileList，解决“上传/已有图片不显示”
  useEffect(() => {
    setFileLists(prev => {
      const next = { ...prev };
      Object.keys(IMAGE_FIELD_LABELS).forEach(field => {
        const rawUrl = watchedUrls[field];
        // 只接受字符串 URL（上传过程中 rc-field-form 可能临时写入 info 对象，需忽略）
        const url = typeof rawUrl === 'string' ? rawUrl : '';
        const current = prev[field] || [];
        const localFile = current[0];
        const localUrl = localFile?.response?.data?.url || localFile?.url;

        if (url) {
          // 本地刚上传（含上传中）且指向同一 URL 时保留本地项，以保留进度和本地缩略图
          if (localFile && (localFile.status === 'uploading' || localUrl === url)) {
            next[field] = current;
          } else {
            next[field] = [{
              uid: `${field}-remote-${url}`,
              name: IMAGE_FIELD_LABELS[field],
              status: 'done',
              url
            }];
          }
        } else {
          // 表单无值：上传中的本地项保留，其余清空
          next[field] = localFile && localFile.status === 'uploading' ? current : [];
        }
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedUrls.id_card_front,
    watchedUrls.id_card_back,
    watchedUrls.wechat_qr_code,
    watchedUrls.alipay_qr_code
  ]);

  const handleUploadChange = (fieldName, info) => {
    // 更新文件列表状态
    setFileLists(prev => ({
      ...prev,
      [fieldName]: info.fileList
    }));

    if (info.fileList.length === 0) {
      // 文件被删除时清空表单值
      form.setFieldsValue({ [fieldName]: undefined });
      return;
    }

    const file = info.fileList[0];
    if (file.status === 'uploading') {
      // 上传中不更新表单值
      return;
    }
    // 上传成功：优先取接口返回的 URL，其次取已有的远程 URL
    const url = file.response?.data?.url || file.url;
    if (url) {
      form.setFieldsValue({ [fieldName]: url });
    }
  };

  // 打开图片预览
  const openImageViewer = (imageUrl, imageName) => {
    if (imageUrl) {
      setCurrentImage({ 
        url: imageUrl, 
        name: imageName || '图片', 
        downloadUrl: imageUrl 
      });
      setImageViewerVisible(true);
    }
  };

  // 关闭图片预览
  const closeImageViewer = () => {
    setImageViewerVisible(false);
    setCurrentImage({ url: '', name: '', downloadUrl: '' });
  };

  // 上传文件到服务器
  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    if (employeeId) {
      formData.append('employee_id', employeeId);
    }
    
    try {
      const response = await fetch('/api/upload/employee', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      const result = await response.json();
      if (result.success) {
        return result.data.url;
      } else {
        throw new Error(result.message || '上传失败');
      }
    } catch (error) {
      console.error('文件上传失败:', error);
      throw error;
    }
  };

  // 创建上传组件的配置函数
  const createUploadProps = (fieldName) => ({
    disabled: !basicEditMode,
    listType: "picture-card",
    maxCount: 1,
    fileList: fileLists[fieldName] || [],
    showUploadList: {
      showPreviewIcon: true,
      showRemoveIcon: basicEditMode,
    },
    onPreview: (file) => {
      const imageUrl = file.response?.data?.url || file.url || file.thumbUrl;
      const imageName = file.name || '图片';
      openImageViewer(imageUrl, imageName);
    },
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        const url = await uploadFile(file);
        onSuccess({ data: { url } });
      } catch (error) {
        onError(error);
      }
    },
    onChange: (info) => handleUploadChange(fieldName, info)
  });

  return (
    <div>
      {/* 身份证和收款码 - 一行 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {/* 身份证正面 */}
        <Col span={6}>
          <Form.Item 
            label={
              <span style={{ fontSize: '12px', fontWeight: 'bold' }}>
                <IdcardOutlined style={{ marginRight: 4, color: '#1890ff' }} />
                身份证正面
              </span>
            } 
            name="id_card_front"
            getValueFromEvent={() => null}
            style={{ marginBottom: 8 }}
          >
            <Upload {...createUploadProps('id_card_front')}>
              {fileLists.id_card_front.length === 0 && (
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                  <UploadOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
                  <div style={{ marginTop: 4, fontSize: '11px' }}>上传正面</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        </Col>

        {/* 身份证反面 */}
        <Col span={6}>
          <Form.Item 
            label={
              <span style={{ fontSize: '12px', fontWeight: 'bold' }}>
                <IdcardOutlined style={{ marginRight: 4, color: '#1890ff' }} />
                身份证反面
              </span>
            } 
            name="id_card_back"
            getValueFromEvent={() => null}
            style={{ marginBottom: 8 }}
          >
            <Upload {...createUploadProps('id_card_back')}>
              {fileLists.id_card_back.length === 0 && (
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                  <UploadOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
                  <div style={{ marginTop: 4, fontSize: '11px' }}>上传反面</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        </Col>

        {/* 微信收款码 */}
        <Col span={6}>
          <Form.Item 
            label={
              <span style={{ fontSize: '12px', fontWeight: 'bold' }}>
                <WechatOutlined style={{ marginRight: 4, color: '#07c160' }} />
                微信收款码
              </span>
            } 
            name="wechat_qr_code"
            getValueFromEvent={() => null}
            style={{ marginBottom: 8 }}
          >
            <Upload {...createUploadProps('wechat_qr_code')}>
              {fileLists.wechat_qr_code.length === 0 && (
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                  <UploadOutlined style={{ fontSize: '20px', color: '#07c160' }} />
                  <div style={{ marginTop: 4, fontSize: '11px' }}>上传微信收款码</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        </Col>

        {/* 支付宝收款码 */}
        <Col span={6}>
          <Form.Item 
            label={
              <span style={{ fontSize: '12px', fontWeight: 'bold' }}>
                <AlipayOutlined style={{ marginRight: 4, color: '#1677ff' }} />
                支付宝收款码
              </span>
            } 
            name="alipay_qr_code"
            getValueFromEvent={() => null}
            style={{ marginBottom: 8 }}
          >
            <Upload {...createUploadProps('alipay_qr_code')}>
              {fileLists.alipay_qr_code.length === 0 && (
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                  <UploadOutlined style={{ fontSize: '20px', color: '#1677ff' }} />
                  <div style={{ marginTop: 4, fontSize: '11px' }}>上传支付宝收款码</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        </Col>
      </Row>

      {/* 银行卡信息 - 4列布局 */}
      <Row gutter={[16, 16]}>
        {/* 工资银行卡号 */}
        <Col span={6}>
          <Form.Item 
            label={
              <span style={{ fontSize: '12px', fontWeight: 'bold' }}>
                <CreditCardOutlined style={{ marginRight: 4, color: '#52c41a' }} />
                工资银行卡号
              </span>
            } 
            name="bank_card_number"
            rules={[
              {
                pattern: /^\d{16,19}$/,
                message: '请输入正确的银行卡号（16-19位数字）'
              }
            ]}
            style={{ marginBottom: 8 }}
          >
            <Input 
              disabled={!basicEditMode} 
              placeholder="请输入银行卡号"
              style={{ fontSize: '12px' }}
            />
          </Form.Item>
        </Col>

        {/* 开户行 */}
        <Col span={6}>
          <Form.Item 
            label={
              <span style={{ fontSize: '12px', fontWeight: 'bold' }}>
                <CreditCardOutlined style={{ marginRight: 4, color: '#52c41a' }} />
                开户行
              </span>
            } 
            name="bank_name"
            style={{ marginBottom: 8 }}
          >
            <Input 
              disabled={!basicEditMode} 
              placeholder="请输入开户行名称"
              style={{ fontSize: '12px' }}
            />
          </Form.Item>
        </Col>

        {/* 空位置，保持布局一致 */}
        <Col span={6}>
          {/* 空位置 */}
        </Col>

        {/* 空位置，保持布局一致 */}
        <Col span={6}>
          {/* 空位置 */}
        </Col>
      </Row>

      {/* 图片预览器 */}
      <ImageViewer
        visible={imageViewerVisible}
        onClose={closeImageViewer}
        imageUrl={currentImage.url}
        imageName={currentImage.name}
        downloadUrl={currentImage.downloadUrl}
      />
    </div>
  );
};

export default EmployeeUploadCard;
